import { useState } from "react";
import { Link } from "react-router-dom";
import { Info, Sparkles, TrendingUp } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

const COLOR = (s) => (s >= 85 ? "#14B8A6" : s >= 70 ? "#14B8A6" : s >= 55 ? "#F59E0B" : "#EF4444");

function Gauge({ score, band }) {
  const c = COLOR(score);
  const r = 54, circ = 2 * Math.PI * r, off = circ - (score / 100) * circ;
  return (
    <div className="relative grid place-items-center">
      <svg width="150" height="150" className="-rotate-90">
        <circle cx="75" cy="75" r={r} fill="none" stroke="#e2e8f0" strokeWidth="12" />
        <circle cx="75" cy="75" r={r} fill="none" stroke={c} strokeWidth="12" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={off} style={{ transition: "stroke-dashoffset .8s ease" }} />
      </svg>
      <div className="absolute text-center">
        <p className="font-display text-3xl font-bold text-[#0B132B]">{score}<span className="text-base text-slate-400">/100</span></p>
        <p className="text-xs font-semibold" style={{ color: c }}>{band}</p>
      </div>
    </div>
  );
}

export function HealthScore({ data }) {
  const [tab, setTab] = useState("Overview");
  const [info, setInfo] = useState(false);
  const explain = data.health_score >= 70
    ? `Your EMI health is ${data.health_band} because most of your payments are being made on time.`
    : "Your EMI health can improve by maintaining timely payments and reducing overdue EMIs.";
  const tabs = ["Overview", "Trend", "Breakdown"];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h2 className="font-display text-lg font-bold text-[#0B132B]">EMI Health Score</h2>
          <button onClick={() => setInfo(!info)} data-testid="health-info" className="text-slate-400 hover:text-[#14B8A6]" aria-label="What is EMI Health Score?"><Info size={16} /></button>
        </div>
        <div className="flex gap-1 rounded-md border border-slate-200 p-1">
          {tabs.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`health-tab-${t.toLowerCase()}`}
              className={`rounded px-3 py-1 text-xs font-semibold ${tab === t ? "bg-[#0B132B] text-white" : "text-slate-500"}`}>{t}</button>
          ))}
        </div>
      </div>

      {info && <div className="mt-3 rounded-md bg-slate-50 p-3 text-xs text-slate-600" data-testid="health-info-box">The EMI Health Score is an <b>informational platform metric</b> (0–100) reflecting your repayment behaviour on ZRo Pe. It considers on-time payments, consistency, overdue history and outstanding burden. It is <b>not</b> an official credit-bureau (e.g. CIBIL) score.</div>}

      <div className="mt-4">
        {tab === "Overview" && (
          <div className="flex flex-col items-center gap-6 sm:flex-row">
            <Gauge score={data.health_score} band={data.health_band} />
            <div className="flex-1">
              <p className="text-sm text-slate-600">{explain}</p>
              <Link to="/ask-ai" state={{ q: "How can I improve my EMI health?" }} data-testid="health-improve" className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-[#14B8A6] px-3 py-2 text-sm font-semibold text-white hover:bg-[#0D9488]"><Sparkles size={15} /> How can I improve?</Link>
            </div>
          </div>
        )}
        {tab === "Trend" && (
          <div>
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="inline-flex items-center gap-1 text-[#14B8A6]"><TrendingUp size={13} /> Last 6 months</span>
              {data.health_trend_sample && <span className="rounded bg-amber-100 px-2 py-0.5 font-bold text-amber-700">SAMPLE</span>}
            </div>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 300, height: 200 }}>
                <AreaChart data={data.health_trend} margin={{ left: -20, right: 6 }}>
                  <defs><linearGradient id="hs" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#14B8A6" stopOpacity={0.3} /><stop offset="100%" stopColor="#14B8A6" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} />
                  <YAxis domain={[40, 100]} tick={{ fontSize: 12, fill: "#64748b" }} />
                  <Tooltip />
                  <Area type="monotone" dataKey="score" stroke="#14B8A6" strokeWidth={2.5} fill="url(#hs)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        {tab === "Breakdown" && (
          <div className="space-y-3">
            {data.health_breakdown?.map((f) => (
              <div key={f.factor} data-testid={`health-factor-${f.factor.replace(/\W+/g, "-").toLowerCase()}`}>
                <div className="flex items-center justify-between text-sm"><span className="font-medium text-[#0B132B]">{f.factor}</span><span className="font-semibold text-[#0B132B]">{f.value}%</span></div>
                <div className="mt-1 h-2 w-full rounded-full bg-slate-100"><div className="h-full rounded-full" style={{ width: `${f.value}%`, background: COLOR(f.value) }} /></div>
                <p className="mt-0.5 text-xs text-slate-400">{f.note}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
